import { Middleware, MiddlewareAPI } from '@reduxjs/toolkit';
import { orderTimerFired } from './order';

const TICKS_EPOCH_OFFSET = 621355968000000000;
const TICKS_PER_MILLISECOND = 10000;
const MAX_TIMEOUT_MS = 2_147_483_000;

const TRIGGER_ACTIONS = new Set<string>([
    'order/getSettings',
    'order/updateSettings',
]);

const ticksToMs = (ticks: number): number =>
    (ticks - TICKS_EPOCH_OFFSET) / TICKS_PER_MILLISECOND;

let timerId: ReturnType<typeof setTimeout> | null = null;
let scheduledEndMs = 0;
let visibilityListenerAttached = false;

const cancelTimer = () => {
    if (timerId !== null) {
        clearTimeout(timerId);
        timerId = null;
    }
};

const scheduleTimer = (store: MiddlewareAPI, endMs: number) => {
    cancelTimer();
    scheduledEndMs = endMs;
    const delta = endMs - Date.now();

    if (delta <= 0) {
        store.dispatch(orderTimerFired());
        return;
    }

    timerId = setTimeout(() => {
        timerId = null;
        if (Date.now() >= scheduledEndMs) {
            store.dispatch(orderTimerFired());
        } else {
            scheduleTimer(store, scheduledEndMs);
        }
    }, Math.min(delta, MAX_TIMEOUT_MS));
};

export const orderTimerMiddleware: Middleware = (store) => {
    if (!visibilityListenerAttached && typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', () => {
            if (
                document.visibilityState === 'visible'
                && scheduledEndMs > 0
                && Date.now() >= scheduledEndMs
            ) {
                cancelTimer();
                store.dispatch(orderTimerFired());
            }
        });
        visibilityListenerAttached = true;
    }

    return (next) => (action: any) => {
        const result = next(action);

        if (TRIGGER_ACTIONS.has(action.type)) {
            const ticks = (store.getState() as any).order?.orderEndsOnTicks ?? 0;

            if (ticks > 0) {
                scheduleTimer(store, ticksToMs(ticks));
            } else {
                cancelTimer();
                scheduledEndMs = 0;
            }
        }

        return result;
    };
};
